import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { FlexRow, Gap } from 'ui-components/components/flex';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { ChoosePhaseBody } from './choose-phase-body';
import messages from '../../messages';
import style from '../../style.module.css';

/**
 * @typedef {import('../../api').MfaEnrollment} MfaEnrollment
 */

/**
 * @param {object} props
 * @param {MfaEnrollment[]} props.mfaEnrollments
 * @param {() => void} props.onCancel
 * @param {(arg0: MfaEnrollment|undefined) => void} props.onChallengeEnrollment
 * @param {boolean} [props.isUnskippable]
 * @param {string} [props.phoneVerificationState]
 * @returns {React.JSX.Element}
 */
export const ChoosePhaseModalContent = ({
  mfaEnrollments,
  onCancel,
  onChallengeEnrollment,
  isUnskippable = false,
  phoneVerificationState,
}) => {
  const [isDisabled, setIsDisabled] = useState(false);
  const [error, setError] = useState(null);
  const [selectedEnrollmentId, setSelectEnrollmentId] = useState(mfaEnrollments[0]?.id);

  const submitEnrollmentChoice = useCallback(async () => {
    if (isDisabled) {
      return;
    }
    setError(null);
    setIsDisabled(true);

    try {
      const selectedEnrollment = mfaEnrollments.find((enrollment) => enrollment.id === selectedEnrollmentId);
      await onChallengeEnrollment(selectedEnrollment);
    } catch (err) {
      setError(err);
      setIsDisabled(false);
    }
  }, [isDisabled, mfaEnrollments, onChallengeEnrollment, selectedEnrollmentId]);

  return (
    <>
      <ModalBody className={style.body}>
        <ChoosePhaseBody
          enrollments={mfaEnrollments}
          error={error}
          phoneVerificationState={phoneVerificationState}
          selectedEnrollmentId={selectedEnrollmentId}
          setSelectEnrollmentId={setSelectEnrollmentId}
        />
      </ModalBody>
      <ModalFooter>
        <FlexRow className={style['footer-buttons']} gap={Gap.Space16}>
          {!isUnskippable && (
            <Button disabled={isDisabled} onClick={onCancel} theme={THEME_CONSTANTS.tertiary}>
              <FormattedMessage {...messages.cancelButton} />
            </Button>
          )}
          <Button disabled={isDisabled} onClick={submitEnrollmentChoice} theme={THEME_CONSTANTS.primary}>
            <FormattedMessage {...messages.sendCodeButton} />
          </Button>
        </FlexRow>
      </ModalFooter>
    </>
  );
};
